/*
 *************************************************************************
 *  ADOBE CONFIDENTIAL
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 *************************************************************************
 */

import {Skeleton} from '@nest/utility/src/skeleton/Skeleton';
import './BenefitsSkeleton.scss';

export class BenefitsSkeleton extends Skeleton {
	static member(): string {
		return `
            <div class="skl-benefits__standalone-member">
                ${this.heading({
									level: 6,
									lines: 2,
									align: {base: 'start', L: 'end'},
								})}
            </div>
        `;
	}

	static memberFeatured(): string {
		return `
            <div class="skl-benefits__standalone-member">
                ${this.heading({level: 5})}
            </div>
        `;
	}

	static v2Card(): string {
		return `
            <div class="skl-benefits-v2-card">
                <div class="skl-benefits-v2-card__header">
                    <div class="skl-benefits-v2-card__icon">
                        ${this.image({aspect: '1/1'})}
                    </div>
                    <div class="skl-benefits-v2-card__eyebrow">
                        ${this.caption()}
                    </div>
                    <div class="skl-benefits-v2-card__title">
                        ${this.heading({level: 4})}
                    </div>
                </div>
                <div class="skl-benefits-v2-card__content">
                    ${this.paragraph({lines: 3})}
                </div>
                <div class="skl-benefits-v2-card__actions">
                    ${this.buttons({justify: {base: 'end'}})}
                </div>
            </div>
        `;
	}

	static v2FeaturedCard(): string {
		return `
            <div class="skl-benefits-v2-featured-card">
                <div class="skl-benefits-v2-featured-card__hero">
                    ${this.image({borderRadius: '0'})}
                </div>
                <div class="skl-benefits-v2-featured-card__content">
                    <div class="skl-benefits-v2-featured-card__logo">
                        ${this.image({aspect: '1/1', borderRadius: '8px'})}
                    </div>
                    <div class="skl-benefits-v2-featured-card__subtitle">
                        ${this.caption()}
                    </div>
                    <div class="skl-benefits-v2-featured-card__title">
                        ${this.heading({level: 4})}
                    </div>
                    <div class="skl-benefits-v2-featured-card__description">
                        ${this.paragraph({lines: 2})}
                    </div>
                    <div class="skl-benefits-v2-featured-card__actions">
                        ${this.buttons({justify: {base: 'end'}})}
                    </div>
                </div>
            </div>
        `;
	}

	static v2FeaturedCards(): string {
		return `
            <div class="skl-benefits-v2__featured-section">
                <div class="skl-benefits-v2__featured-title">
                    ${this.heading({level: 4})}
                </div>
                <div class="skl-benefits-v2__featured">
                    ${this.v2FeaturedCard()}
                    ${this.v2FeaturedCard()}
                </div>
            </div>
        `;
	}

	static toStringV2(): string {
		return this.wrapper({
			className: 'skl-benefits-v2',
			children: `
                <section class="skl-benefits-v2__header">
                    <div class="skl-benefits-v2__header-row">
                        <div class="skl-benefits-v2__title">
                            ${this.heading({level: 3})}
                        </div>
                        <div class="skl-benefits-v2__user-info">
                            ${this.heading({level: 5})}
                        </div>
                    </div>
                    <div class="skl-benefits-v2__header-text">
                        <div class="skl-benefits-v2__subtitle">
                            ${this.paragraph({lines: 2})}
                        </div>
                        <div class="skl-benefits-v2__disclaimer">
                            ${this.caption()}
                        </div>
                    </div>
                    <div class="skl-benefits-v2__filters">
                        ${this.buttons({count: 4, type: 'action'})}
                    </div>
                </section>
                <div class="skl-benefits-v2__sections">
                    <div class="skl-benefits-v2__featured-section">
                        <div class="skl-benefits-v2__featured-title">
                            ${this.heading({level: 4})}
                        </div>
                        <div class="skl-benefits-v2__featured">
                            ${this.v2FeaturedCard()}
                            ${this.v2FeaturedCard()}
                        </div>
                    </div>
                    <div class="skl-benefits-v2__category">
                        <div class="skl-benefits-v2__category-title">
                            ${this.heading({level: 4})}
                        </div>
                        <div class="skl-benefits-v2__category-cards">
                            ${[...Array(4).keys()].map(() => this.v2Card()).join('')}
                        </div>
                    </div>
                    <div class="skl-benefits-v2__category">
                        <div class="skl-benefits-v2__category-title">
                            ${this.heading({level: 4})}
                        </div>
                        <div class="skl-benefits-v2__category-cards">
                            ${[...Array(4).keys()].map(() => this.v2Card()).join('')}
                        </div>
                    </div>
                    <div class="skl-benefits-v2__category">
                        <div class="skl-benefits-v2__category-title">
                            ${this.heading({level: 4})}
                        </div>
                        <div class="skl-benefits-v2__category-cards">
                            ${[...Array(4).keys()].map(() => this.v2Card()).join('')}
                        </div>
                    </div>
                </div>
            `,
		});
	}
}
